import React, { useState } from "react";
import classNames from "classnames/bind";
import { Form, Field } from "react-final-form";
import useSWR from "swr";

import { CATEGORIES } from "../../api/categories";

import { Screen } from "../../components/Screen";
import { Input } from "../../components/Input";
import { InputDate } from "../../components/InputDate";
import { InputPhone } from "../../components/InputPhone";
import { RadioGroup } from "../../components/RadioGroup";
import { MobileSelect } from "../../components/MobileSelect";
import { AnthropometryFields } from "components/AnthropometryFields";
import { PageLoader } from "components/PageLoader";

import { useStateManager } from "utils/useStateManager";

import { UserType } from "types/UserType";

import { createValidation, epharmacyFormValidator } from "./validation";

import styles from "./Registration.module.css";

const cx = classNames.bind(styles);

const radioGroupSex = [
  {
    value: "1",
    label: "Мужской",
  },
  {
    value: "2",
    label: "Женский",
  },
];

export enum Smoking {
  YES = "YES",
  NO = "NO",
}

export type FormValues = {
  custom_lastname: string;
  custom_firstname: string;
  custom_midname: string;
  custom_дата_рождения: string;
  custom_sexint_save: string;
  custom_login: string;
  custom_телефон: string;
  custom_страна?: string;
  custom_город?: string;
  custom_адрес: string;
  custom_масса_тела: string;
  custom_рост: string;
  custom_детская_жизненная_активность?: string;
  custom_жизненная_активность?: string;
  custom_спортивная_автивность?: string;
  smoking: Smoking;
};

type Props = {
  onSubmit: (exists?: boolean) => void;
};

type FormFieldProps = React.ComponentProps<typeof Input> & {
  name: keyof FormValues;
};

const FormField: React.FC<FormFieldProps> = (props) => (
  <Field name={props.name}>
    {({ input, meta }) => (
      <Input
        {...input}
        label={props.label}
        required={props.required}
        mask={props.mask}
        error={meta.touched ? meta.error : undefined}
      />
    )}
  </Field>
);

const calculateAge = (birthDay: number, birthMonth: number, birthYear: number) => {
  const nuw = new Date();
  const currentYear = nuw.getFullYear();

  let age = currentYear - birthYear;

  return age;
}
const getIsChild = (dateString: string) => {
  const parts = dateString.split(".").map((item) => parseInt(item, 10)) as [
    number,
    number,
    number
  ];

  const age = calculateAge(...parts);

  return age < 18;
};

export const Registration: React.FC<Props> = (props) => {
  const { userType, linkInfo, patient, updatePatient, isLoading, isInitial } = useStateManager();
  const isEpharmacy = userType === UserType.LITE && linkInfo?.custom?.custom_генерировать_бад_рацион_при_саморегистрации;
  const [isChild, setIsChild] = useState<boolean>(false);
  const [birthdayDate, setBirthdayDate] = useState<string>("");

  let isRequiredPhone = true;
  let isRequiredEmail = userType === UserType.PRO;
  if (linkInfo?.custom) {
    isRequiredPhone = linkInfo?.custom?.custom_обязательность_телефон || false;
    isRequiredEmail = linkInfo?.custom?.custom_обязательность_эл_почта || false;
  }

  const { data: dataCountries = [] } = useSWR<
    {
      key: string;
      custom_название: string;
    }[]
  >([CATEGORIES.COUNTRY, 301]);
  const { data: dataCities = [] } = useSWR<
    {
      key: string;
      custom_название: string;
      custom_страна: { key: string } | null;
    }[]
  >([CATEGORIES.CITY, 300]);

  if (isEpharmacy) {
    const handleSubmit = ({ smoking, ...formValues }: FormValues) => {
      const illness = {
        [Smoking.YES]: ["c105a902t151r6091781"],
        [Smoking.NO]: [],
      };

      updatePatient({
        ...formValues,
        custom_болезни: illness[smoking],
        custom_firstname: "ㅤ",
        custom_lastname: "ㅤ",
        custom_телефон: "+77777777777"
      });

      props.onSubmit();
    };
    if(isInitial) props.onSubmit();
    if (isLoading && isEpharmacy) {
      return (
        <PageLoader
          title={"Генерация отчёта и рекомендации"}
          description={"Это может занять некоторое время"}
          duration={45}
        />
      )
    }

    return (
      <Form<FormValues>
        initialValues={
          patient || {
            custom_масса_тела: "",
            custom_рост: "",
          }
        }
        validate={epharmacyFormValidator}
        validateOnMount
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, valid, values, submitting }) => {
          return (
            <Screen
              title="Пару слов о себе"
              buttonTitle="Продолжить"
              onSubmit={handleSubmit}
              isInvalid={!valid}
              isLoading={submitting}
            >
              <div className={cx("disclaimer")}>
                <span className={cx("disclaimer__inner")}>
                  Заполните все поля
                </span>
              </div>
              <div className={cx("form-body")}>
                <Field name="custom_дата_рождения">
                  {({ input, meta }) => (
                    <InputDate
                      {...input}
                      label="Дата рождения"
                      required
                      error={meta.touched ? meta.error : undefined}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        const isChildEntered = getIsChild(event.target.value);
                        if (isChildEntered !== isChild) {
                          values.custom_жизненная_активность = "";
                          values.custom_спортивная_автивность = "";
                          values.custom_детская_жизненная_активность = "";
                          setIsChild(isChildEntered);
                        }
                        setBirthdayDate(event.target.value);
                        input.onChange(event.target.value);
                      }}
                      value={input.value}
                    />
                  )}
                </Field>
                <Field name="custom_sexint_save">
                  {(props) => (
                    <div className={cx("m-radio")}>
                      <RadioGroup
                        name={props.input.name}
                        label="Пол"
                        required
                        items={radioGroupSex}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          props.input.onChange(event.target.value);
                        }}
                        value={props.input.value}
                      />
                    </div>
                  )}
                </Field>
                {birthdayDate.split(".").join("").replace("_", "").length === 8 &&
                  <AnthropometryFields isChild={isChild} />
                }
              </div>
            </Screen>
          );
        }}
      </Form >
    );
  }

  const handleSubmit = (formValues: FormValues) => {
    updatePatient(formValues);
    props.onSubmit();
  };

  const hasCustomCities = !!linkInfo?.custom?.custom_города;


  return (
    <Form<FormValues>
      initialValues={{
        ...patient!,
        custom_страна:
          patient?.custom_страна || linkInfo?.custom?.custom_страны?.[0].key,
      }}
      validate={createValidation(userType, hasCustomCities, isRequiredPhone, isRequiredEmail)}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, valid, values, submitting }) => {
        const countries =
          linkInfo?.custom?.custom_страны?.map((item) => ({
            value: item.key,
            label: item.label,
          })) ||
          dataCountries.map((item) => ({
            value: item.key,
            label: item.custom_название,
          }));

        const cities =
          linkInfo?.custom?.custom_города?.map((item) => ({
            value: item.key,
            label: item.label,
          })) ||
          dataCities
            .filter((item) => item.custom_страна?.key === values.custom_страна)
            .map((item) => ({
              value: item.key,
              label: item.custom_название,
            }));

        return (
          <Screen
            title="Сейчас мы вас зарегистрируем"
            buttonTitle="Продолжить"
            onSubmit={handleSubmit}
            isInvalid={!valid}
            isLoading={submitting}
          >
            <div className={cx("disclaimer")}>
              <span className={cx("disclaimer__inner")}>
                Поля отмеченные звездочкой (
                <span className={cx("required")}>
                  <span className={cx("red-star")}>*</span>
                </span>
                ) обязательны для заполнения
              </span>
            </div>
            <div className={cx("form-body")}>
              <FormField name="custom_lastname" label="Фамилия" required />
              <FormField name="custom_firstname" label="Имя" required />
              <FormField name="custom_midname" label="Отчество" />
              <Field name="custom_дата_рождения">
                {({ input, meta }) => (
                  <InputDate
                    {...input}
                    label="Дата рождения"
                    required
                    error={meta.touched ? meta.error : undefined}
                  />
                )}
              </Field>
              <Field name="custom_sexint_save">
                {(props) => (
                  <div className={cx("m-radio")}>
                    <RadioGroup
                      name={props.input.name}
                      label="Пол"
                      required
                      items={radioGroupSex}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        props.input.onChange(event.target.value);
                      }}
                      value={props.input.value}
                    />
                  </div>
                )}
              </Field>

              <div className={cx("disclaimer", "disclaimer-additional")}>
                <span className={cx("disclaimer__inner")}>
                  Почта и телефон нужны для связи консультанта с вами
                </span>
              </div>
              <FormField
                name="custom_login"
                label={linkInfo?.custom?.custom_название_поля_эл_почта || "Эл.почта"}
                required={isRequiredEmail}
              />
              <Field name="custom_телефон">
                {({ input, meta }) => (
                  <InputPhone
                    {...input}
                    label="Телефон"
                    required={isRequiredPhone}
                    error={meta.touched ? meta.error : undefined}
                  />
                )}
              </Field>
              {countries.length > 1 && (
                <Field name="custom_страна">
                  {({ input }) => (
                    <MobileSelect
                      searchable
                      options={countries}
                      placeholder="Страна"
                      {...input}
                    />
                  )}
                </Field>
              )}
              <Field name="custom_город">
                {({ input }) => (
                  <MobileSelect
                    required={hasCustomCities}
                    searchable
                    disabled={!values.custom_страна}
                    options={cities}
                    extraOptions={
                      linkInfo?.custom?.custom_города
                        ? []
                        : dataCities
                          .filter((item) => !item.custom_страна)
                          .map((item) => ({
                            value: item.key,
                            label: item.custom_название,
                          }))
                    }
                    placeholder="Город"
                    {...input}
                  />
                )}
              </Field>
              <FormField name="custom_адрес" label="Адрес" />
            </div>
          </Screen>
        );
      }}
    </Form>
  );
};
